// src/components/settings/sections/BillingSettings.js
import React from 'react';
import {
    CreditCardIcon,
    InformationCircleIcon,
    DocumentTextIcon,
    TagIcon,
    CalendarDaysIcon, CurrencyDollarIcon, DocumentArrowDownIcon, CheckCircleIcon, ExclamationCircleIcon, ClockIcon
} from '@heroicons/react/24/outline';

// Mock Data for Invoices
const mockInvoices = [
    {
        id: 'inv_001',
        invoiceNumber: 'LL-20250615-001',
        date: new Date('2025-06-15'),
        amount: 99.00,
        status: 'Paid',
        description: 'Monthly Subscription - June 2025',
        downloadLink: '#invoice_001.pdf' // Placeholder link
    },
    {
        id: 'inv_002',
        invoiceNumber: 'LL-20250515-001',
        date: new Date('2025-05-15'),
        amount: 99.00,
        status: 'Paid',
        description: 'Monthly Subscription - May 2025',
        downloadLink: '#invoice_002.pdf'
    },
    {
        id: 'inv_003',
        invoiceNumber: 'LL-20250415-001',
        date: new Date('2025-04-15'),
        amount: 99.00,
        status: 'Overdue',
        description: 'Monthly Subscription - April 2025',
        downloadLink: '#invoice_003.pdf'
    },
    {
        id: 'inv_004',
        invoiceNumber: 'LL-20250315-001',
        date: new Date('2025-03-15'),
        amount: 99.00,
        status: 'Paid',
        description: 'Monthly Subscription - March 2025',
        downloadLink: '#invoice_004.pdf'
    },
    {
        id: 'inv_005',
        invoiceNumber: 'LL-20250215-001',
        date: new Date('2025-02-15'),
        amount: 99.00,
        status: 'Pending',
        description: 'Monthly Subscription - Feb 2025',
        downloadLink: '#invoice_005.pdf'
    },
];

const getStatusClasses = (status) => {
    switch (status) {
        case 'Paid':
            return 'bg-green-100 text-green-800';
        case 'Overdue':
            return 'bg-red-100 text-red-800';
        case 'Pending':
            return 'bg-yellow-100 text-yellow-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

const getStatusIcon = (status) => {
    switch (status) {
        case 'Paid':
            return <CheckCircleIcon className="h-5 w-5 text-green-500 mr-1" />;
        case 'Overdue':
            return <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-1" />;
        case 'Pending':
            return <ClockIcon className="h-5 w-5 text-yellow-500 mr-1" />;
        default:
            return null;
    }
};

const BillingSettings = () => {
    // Mock data
    const currentPlan = "Pro Plan";
    const monthlyCost = "ZAR499.00/month";
    const nextBillingDate = "July 1, 2025";
    const activeCustomersCount = "1,850 / 2,000"; // Example usage metric

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-800">Billing & Plan</h2>
            <p className="text-gray-600">Manage your subscription, payment methods, and view billing history.</p>

            {/* Current Plan Details */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <InformationCircleIcon className="h-6 w-6 text-indigo-500 mr-2" />
                    Your Current Plan
                </h3>
                <p className="text-sm text-gray-700">You are currently on the <strong className="text-indigo-600">{currentPlan}</strong>.</p>
                <div className="flex justify-between items-center text-sm text-gray-700">
                    <span>Monthly Cost:</span>
                    <span className="font-semibold">{monthlyCost}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-700">
                    <span>Next Billing Date:</span>
                    <span className="font-semibold">{nextBillingDate}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-700">
                    <span>Active Customers:</span>
                    <span className="font-semibold">{activeCustomersCount}</span>
                </div>
                <div className="pt-4 border-t border-gray-200">
                    <button disabled={true}
                        onClick={() => alert('Navigate to Change Plan page/modal')}
                        className="inline-flex cursor-not-allowed justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Change Plan
                    </button>
                </div>
            </div>

            {/* Payment Method */}
            <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
                    <CreditCardIcon className="h-6 w-6 text-gray-500 mr-2" />
                    Payment Method
                </h3>
                <p className="text-sm text-gray-700 mb-4">Your current payment method: **** **** **** 4242 (Visa)</p>
                <button
                    onClick={() => alert('Navigate to Update Payment Method form')}
                    className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                    Update Payment Method
                </button>
            </div>

            {/* Billing History */}
            <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
                    <DocumentTextIcon className="h-6 w-6 text-gray-500 mr-2" />
                    Billing History
                </h3>
                <p className="text-sm text-gray-700 mb-4">View and download your past invoices.</p>
                {mockInvoices.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">
                        <p className="text-xl">No billing history found.</p>
                        <p className="text-md mt-2">Your invoices will appear here after your first payment.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Invoice
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount (ZAR)
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {mockInvoices.map((invoice) => (
                                <tr key={invoice.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        <div className="flex items-center">
                                            <TagIcon className="h-5 w-5 text-gray-400 mr-2" />
                                            {invoice.invoiceNumber}
                                        </div>
                                        <div className="text-gray-500 text-xs mt-1">{invoice.description}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex items-center">
                                            <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-2" />
                                            {invoice.date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex items-center">
                                            <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                                            R{invoice.amount.toFixed(2)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClasses(invoice.status)}`}
                      >
                        {getStatusIcon(invoice.status)} {invoice.status}
                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <a
                                            href={invoice.downloadLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                                            Download
                                        </a>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BillingSettings;